/*
Istanbul ignore is used to bypass test coverage reduction
caused by the amount of getter / setter functions.
*/
/* istanbul ignore file */
import { Properties } from '@/types/contact-form-page.types'
import { ActionContext, Store, Module } from 'vuex'
import {
  submitAuthentication,
  requestAuthenticationCancellation,
  collectStatus,
  getUserData,
  STATUS_FAILED
} from '@/services/authentication.v0.service'
import { Namespace } from '@/store/Namespaces'
import {
  setAuthenticationToken,
  getAuthenticationToken,
  removeAuthenticationToken
} from '@/services/local-storage.service'

export enum Setters {
  SetFullName = 'SetFullName',

  SetIsRequestOngoing = 'SetIsRequestOngoing',
  SetIsFormSubmitted = 'SetIsFormSubmitted',
  SetErrorResponse = 'SetErrorResponse',

  SetPersonalIdentityNumber = 'SetPersonalIdentityNumber',
  SetIsAuthenticationOngoing = 'SetIsAuthenticationOngoing',
  SetIsAuthenticated = 'SetIsAuthenticated',
  SetAuthenticationError = 'SetAuthenticationError',
  SetAuthenticationWarning = 'SetAuthenticationWarning',
  SetIsCancellationOngoing = 'SetIsCancellationOngoing',

  SetToken = 'SetToken'
}

interface ContactFormState {
  [Properties.FullName]: string;
  [Properties.PersonalIdentityNumber]: string;

  [Properties.IsAuthenticated]: boolean;
  [Properties.IsAuthenticationOngoing]: boolean;
  [Properties.AuthenticationError]: Error | null;
  [Properties.AuthenticationWarning]: string | null;
  [Properties.IsCancellationOngoing]: boolean;

  [Properties.Token]: string;
}

const ERROR_MESSAGE_TRANSACTION_IS_CANCELED = 'Transaktionen är avbruten'
const ERROR_MESSAGE_AUTHENTICATION_FAILED = 'BankID-autentisering misslyckades av tekniska skäl'

export type VuexActionContext = ActionContext<ContactFormState, {}>
type AuthenticationV0Store = Store<ContactFormState>

const namespace = (s: string): string => `${Namespace.AuthenticationV0}/${s}`

export const getIsAuthenticated = (store: AuthenticationV0Store): boolean => store.getters[namespace(Properties.IsAuthenticated)]
export const getIsAuthenticationOngoing = (store: AuthenticationV0Store): boolean => store.getters[namespace(Properties.IsAuthenticationOngoing)]
export const getAuthenticationError = (store: AuthenticationV0Store): string | null => store.getters[namespace(Properties.AuthenticationError)]
export const getAuthenticationWarning = (store: AuthenticationV0Store): string | null => store.getters[namespace(Properties.AuthenticationWarning)]
export const getIsCancellationOngoing = (store: AuthenticationV0Store): boolean => store.getters[namespace(Properties.IsCancellationOngoing)]
export const getFullName = (store: AuthenticationV0Store): string => store.getters[namespace(Properties.FullName)]
export const getPersonalIdentityNumber = (store: AuthenticationV0Store): string => store.getters[namespace(Properties.PersonalIdentityNumber)]
export const getToken = (store: AuthenticationV0Store): string => store.getters[namespace(Properties.Token)]

export const setAuthenticationError = (store: AuthenticationV0Store, authenticationError: Error | null): void => store.commit(namespace(Setters.SetAuthenticationError), authenticationError)
export const setAuthenticationWarning = (store: AuthenticationV0Store, authenticationWarning: Error | null): void => store.commit(namespace(Setters.SetAuthenticationWarning), authenticationWarning)
export const setPersonalIdentityNumber = (store: AuthenticationV0Store, personalIdentityNumber: string): void => store.commit(namespace(Setters.SetPersonalIdentityNumber), personalIdentityNumber)

export const requestAuthentication = (store: AuthenticationV0Store): Promise<void> => store.dispatch(namespace(Properties.RequestAuthentication))
export const cancelAuthentication = (store: AuthenticationV0Store): Promise<void> => store.dispatch(namespace(Properties.CancelAuthentication))
export const initAuthStore = (store: AuthenticationV0Store): Promise<void> => store.dispatch(namespace(Properties.Init))

const AuthenticationV2Store: Module<ContactFormState, {}> = {
  namespaced: true,
  state: {
    [Properties.FullName]: '',
    [Properties.PersonalIdentityNumber]: '',

    [Properties.IsAuthenticated]: false,
    [Properties.IsAuthenticationOngoing]: false,
    [Properties.IsCancellationOngoing]: false,
    [Properties.AuthenticationError]: null,
    [Properties.AuthenticationWarning]: null,

    [Properties.Token]: ''
  },
  getters: {
    [Properties.FullName]: (state: ContactFormState) => state.fullName,
    [Properties.PersonalIdentityNumber]: (state: ContactFormState) => state.personalIdentityNumber,

    [Properties.IsAuthenticated]: (state: ContactFormState) => state.isAuthenticated,
    [Properties.IsAuthenticationOngoing]: (state: ContactFormState) => state.isAuthenticationOngoing,
    [Properties.IsCancellationOngoing]: (state: ContactFormState) => state.isCancellationOngoing,
    [Properties.AuthenticationError]: (state: ContactFormState) => state.authenticationError,
    [Properties.AuthenticationWarning]: (state: ContactFormState) => state.authenticationWarning,

    [Properties.Token]: (state: ContactFormState) => state.token
  },
  mutations: {
    [Setters.SetFullName]: (state: ContactFormState, fullName: string) => state.fullName = fullName,
    [Setters.SetPersonalIdentityNumber]: (state: ContactFormState, personalIdentityNumber: string) => state.personalIdentityNumber = personalIdentityNumber,

    [Setters.SetIsAuthenticated]: (state: ContactFormState, isAuthenticated: boolean) => {
      state.isAuthenticated = isAuthenticated
    },
    [Setters.SetIsAuthenticationOngoing]: (state: ContactFormState, isAuthenticationOngoing: boolean) => {
      state.isAuthenticationOngoing = isAuthenticationOngoing
    },
    [Setters.SetIsCancellationOngoing]: (state: ContactFormState, isCancellationOngoing: boolean) => {
      state.isCancellationOngoing = isCancellationOngoing
    },
    [Setters.SetAuthenticationError]: (state: ContactFormState, authenticationError: Error | null) => {
      state.authenticationError = authenticationError
    },
    [Setters.SetAuthenticationWarning]: (state: ContactFormState, authenticationWarning: string | null) => {
      state.authenticationWarning = authenticationWarning
    },

    [Setters.SetToken]: (state: ContactFormState, token: string) => {
      state.token = token
    }
  },
  actions: {
    [Properties.Authenticate]: ({ commit, state }: ActionContext<ContactFormState, {}>): void => {
      commit(Setters.SetAuthenticationError, null)
      commit(Setters.SetAuthenticationWarning, null)

      setAuthenticationToken(state.token)

      commit(Setters.SetIsAuthenticated, true)
      commit(Setters.SetIsAuthenticationOngoing, false)
    },
    [Properties.CancelAuthentication]: async ({
      commit,
      state
    }: ActionContext<ContactFormState, {}>): Promise<void> => {
      try {
        commit(Setters.SetIsCancellationOngoing, true)
        await requestAuthenticationCancellation(state.token)
        commit(Setters.SetIsAuthenticated, false)
      } catch (err) {
        commit(Setters.SetAuthenticationError, err)
      }
    },
    [Properties.CollectStatus]: async ({
      commit,
      state,
      dispatch
    }: ActionContext<ContactFormState, {}>): Promise<void> => {
      try {
        commit(Setters.SetAuthenticationError, null)
        commit(Setters.SetAuthenticationWarning, null)
        commit(Setters.SetIsAuthenticationOngoing, true)

        const data = await collectStatus(state.token)

        if (data.status === STATUS_FAILED) {
          commit(Setters.SetIsCancellationOngoing, false)
          commit(Setters.SetIsAuthenticationOngoing, false)
          commit(Setters.SetIsAuthenticated, false)
          commit(Setters.SetAuthenticationWarning, ERROR_MESSAGE_TRANSACTION_IS_CANCELED)
        } else {
          dispatch(Properties.FetchUser)
        }
      } catch {
        commit(Setters.SetIsAuthenticationOngoing, false)
        commit(Setters.SetIsAuthenticated, false)
        commit(Setters.SetAuthenticationError, ERROR_MESSAGE_AUTHENTICATION_FAILED)
      }
    },
    [Properties.EndSession]: ({ commit }: ActionContext<ContactFormState, {}>): void => {
      removeAuthenticationToken()
      commit(Setters.SetToken, '')
      commit(Setters.SetAuthenticationError, null)
      commit(Setters.SetAuthenticationWarning, null)
      commit(Setters.SetIsAuthenticationOngoing, false)
      commit(Setters.SetIsAuthenticated, false)
    },
    [Properties.FetchUser]: async ({
      commit,
      dispatch,
      state
    }: ActionContext<ContactFormState, {}>): Promise<void> => {
      try {
        const data = await getUserData(state.token)
        commit(Setters.SetFullName, data.name)
        commit(Setters.SetPersonalIdentityNumber, data.personalNumber)
        dispatch(Properties.Authenticate)
      } catch {
        dispatch(Properties.EndSession)
      }
    },
    [Properties.Init]: async ({
      commit,
      dispatch
    }: ActionContext<ContactFormState, {}>): Promise<void> => {
      const token = getAuthenticationToken()

      if (token) {
        commit(Setters.SetToken, token)
        await dispatch(Properties.FetchUser)
      }
    },
    [Properties.RequestAuthentication]: async ({
      commit,
      state,
      dispatch
    }: ActionContext<ContactFormState, {}>): Promise<void> => {
      try {
        commit(Setters.SetAuthenticationError, null)
        commit(Setters.SetAuthenticationWarning, null)
        commit(Setters.SetIsAuthenticationOngoing, true)

        const data = await submitAuthentication(state.personalIdentityNumber)

        commit(Setters.SetToken, data.token)
        dispatch(Properties.CollectStatus)
      } catch {
        commit(Setters.SetAuthenticationError, ERROR_MESSAGE_AUTHENTICATION_FAILED)
        commit(Setters.SetIsAuthenticationOngoing, false)
      }
    }
  }
}

export default AuthenticationV2Store
